import React, { useEffect, useRef, useMemo } from "react";
import { TabulatorFull } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/table_styles.css";
import config from "../config.js";
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
async function downloadCSV(viewID, downloadFileName, httpHeaders) {
  // or cookie

  const res = await fetch(`${config.BACKEND_URL}/views/${viewID}/export/csv`, {
    method: "GET",
    headers: {
      ...httpHeaders,
    },
  });

  if (!res.ok) {
    alert("Failed to download CSV");
    return;
  }

  // Convert response stream → blob
  const blob = await res.blob();

  // Create a temporary link to trigger file download
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${downloadFileName}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const Table = ({
  shouldDisplay = true,
  isSelectable = true,
  tableData = [],
  tableColumns = [],
  handleDeleteButton,
  handleEditButton,
  shouldModifySources,
  shouldDownload,
  displaySerial = false,
  setParentRows = () => {},
  onSelectionChange = () => {},
  highlightedRows = [],
  tableID = "data",
  edit,
  deletee,
  selectable = true,
  layoutMode = "fitColumns",
  tableHeight = null,
  rowFormatter = null,
  responsiveLayoutMode = false,
  viewName = "", // New prop for view name
  dateRange = "", // New prop for date range
  viewID = null, // New prop for view ID
  httpHeaders = null, // New prop for HTTP headers
}) => {
  const tableRef = useRef(null);
  const selectionInProgress = useRef(false);
  const latestSelectedData = useRef([]);

  const debouncedSetParentRows = useMemo(
    () => debounce(onSelectionChange, 100),
    [onSelectionChange]
  );

  useEffect(() => {
    if (!shouldDisplay || !tableData || tableData.length === 0) return;

    const tableElement = document.getElementById(tableID);
    if (!tableElement) return;

    const setColumns = (columns) => {
      let newColumns = [];
      if (displaySerial) {
        newColumns.push({ formatter: "rownum", hozAlign: "center", width: 70 });
      }
      if (isSelectable) {
        newColumns.unshift({
          formatter: "rowSelection",
          titleFormatter: "rowSelection",
          hozAlign: "center",
          headerSort: false,
          width: 70,
        });
      }
      newColumns.push(
        ...columns.map((col) => ({
          ...col,
          minWidth: col.minWidth || 100,
          maxWidth: col.maxWidth || 400,
          resizable: true,
          widthGrow: 0,
          widthShrink: 0,
        }))
      );
      return newColumns;
    };

    const highlightRowIds = Array.isArray(highlightedRows)
      ? highlightedRows.map((id) => (typeof id === "object" ? id.id : id))
      : [];

    const table = new TabulatorFull(`#${tableID}`, {
      layout: layoutMode,
      height: tableHeight,
      rowFormatter: rowFormatter,
      columns: setColumns(tableColumns),
      data: tableData,
      pagination: "local",
      paginationSize: 10,
      paginationSizeSelector: [10, 25, 50, 100],
      tooltips: true,
      responsiveLayout: responsiveLayoutMode,
      selectableRows: selectable === "single" ? 1 : isSelectable, // <--- FIX HERE
      resizableColumns: true,
      autoColumns: false,
    });

    table.on("tableBuilt", function () {
      // Add the Download as CSV button to the footer if shouldDownload is true
      if (shouldDownload) {
        const paginator = document.querySelector(
          `#${tableID} .tabulator-footer .tabulator-paginator`
        );
        if (paginator) {
          const downloadButton = document.createElement("button");
          downloadButton.className = "download-csv-btn";
          downloadButton.textContent = "Download as CSV";
          downloadButton.type = "button";
          paginator.appendChild(downloadButton);

          downloadButton.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Construct the filename with viewName and dateRange if provided
            let downloadFileName = [undefined, null, ""].includes(tableID)
              ? "data"
              : tableID;
            if (viewName && dateRange) {
              downloadFileName = `${viewName}_${dateRange}`;
            } else if (viewName) {
              downloadFileName = `${viewName}_${downloadFileName}`;
            }
            // table.download("csv", `${downloadFileName}.csv`, {
            //   columnGroups: true,
            //   rowGroups: true,
            //   columnCalcs: true,
            // });
            downloadCSV(viewID, downloadFileName, httpHeaders);
          });
        }
      }

      // Handle row highlighting
      if (highlightRowIds.length > 0) {
        const rowsToSelect = table.getRows().filter((row) => {
          const rowData = row.getData();
          return highlightRowIds.includes(rowData.id);
        });
        if (rowsToSelect.length > 0) {
          selectionInProgress.current = true;
          table.selectRow(rowsToSelect);
          latestSelectedData.current = table.getSelectedData();
          debouncedSetParentRows(latestSelectedData.current);
          selectionInProgress.current = false;
        }
      }
    });

    table.on("rowSelectionChanged", function (data, rows) {
      if (selectionInProgress.current) return;
      selectionInProgress.current = true;
      const selectedData = table.getSelectedData();
      const currentIds = selectedData
        .map((row) => row.id)
        .sort()
        .join(",");
      const prevIds = latestSelectedData.current
        .map((row) => row.id)
        .sort()
        .join(",");
      if (currentIds !== prevIds) {
        latestSelectedData.current = selectedData;
        debouncedSetParentRows(selectedData);

        selectionInProgress.current = false;
      } else {
        selectionInProgress.current = false;
      }
    });

    tableRef.current = table;
    return () => {
      if (tableRef.current) {
        tableRef.current.destroy();
        tableRef.current = null;
      }
    };
  }, [
    tableData,
    tableID,
    tableColumns,
    debouncedSetParentRows,
    shouldDisplay,
    isSelectable,
    shouldDownload,
    displaySerial,
    highlightedRows,
    selectable,
    layoutMode,
    tableHeight,
    rowFormatter,
    responsiveLayoutMode,
    viewName,
    dateRange,
  ]);

  return (
    <>
      {shouldDisplay && (
        <div
          style={{
            overflowX: "auto",
            width: "100%",
            minWidth: "0",
            whiteSpace: "nowrap",
          }}
        >
          <div id={tableID} key={tableID} style={{ minWidth: "100%" }}></div>
        </div>
      )}
      {shouldModifySources && (
        <section className="d-flex flex-row-reverse mb-3 mt-2">
          {shouldModifySources && (
            <section>
              {deletee && (
                <button className="btn btn-danger" onClick={handleDeleteButton}>
                  Delete
                </button>
              )}
              {edit && (
                <button className="btn btn-primary" onClick={handleEditButton}>
                  Edit
                </button>
              )}
            </section>
          )}
        </section>
      )}
    </>
  );
};

export default React.memo(Table);
